import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { useStore } from "@shared/store/useStore";
import { getPlatform } from "@shared/utils/get-platform";

type RefreshTokenResponse = { pk: number; token: string };

/**
 * Factory class for making HTTP requests.
 * Handles the logic for sending JWT requests on MOBILE, and session requests on WEB/DESKTOP.
 */
class HttpRequestFactory {
  private static instance: AxiosInstance;

  /**
   * Sends an HTTP request using the provided Axios instance.
   * - WEB and DESKTOP use session/cookie auth
   * - MOBILE uses JWT auth
   */
  public static async request<Response = unknown>(
    instance: AxiosInstance,
    requestData: AxiosRequestConfig
  ) {
    this.instance = instance;
    const platform = getPlatform();

    // Add the request interceptor for platforms using session auth
    if (platform === "WEB" || platform === "DESKTOP") {
      this.addSessionAuthRequestInterceptor();
    }

    if (platform === "MOBILE") {
      return this.jwtRequest<Response>(requestData);
    }

    return this.sessionRequest<Response>(requestData);
  }

  private static async jwtRequest<Response = unknown>(
    requestData: AxiosRequestConfig
  ) {
    if (requestData?.url) {
      HttpRequestFactory.checkAndSetJwtHeaders(requestData.url);
    }

    try {
      return this.instance.request<Response>(requestData);
    } catch (error) {
      return HttpRequestFactory.retryWithToken(error, requestData);
    }
  }

  private static async sessionRequest<Response = unknown>(
    requestData: AxiosRequestConfig
  ) {
    return this.instance.request<Response>(requestData);
  }

  /**
   * Sets the proper headers in the request if JWTs should be used.
   */
  private static checkAndSetJwtHeaders(requestUrl: string): void {
    // TODO: should be handled more gracefully by backend
    const isPublicEndpoint = requestUrl.startsWith("/api/public/");

    if (isPublicEndpoint) {
      return;
    }

    const { jwt } = useStore.getState();
    if (jwt) {
      this.instance.defaults.headers.common.Authorization = `JWT ${jwt}`;
    }
  }

  private static async refreshToken(
    token?: string
  ): Promise<RefreshTokenResponse> {
    const { data } = await this.instance.post("/api/auth/jwt/refresh/", {
      token,
    });

    return data;
  }

  /**
   * Handles the retrying of requests with a refreshed JWT token.
   */
  private static async retryWithToken(
    error: unknown,
    requestData: AxiosRequestConfig
  ) {
    const { jwt, setJwt, setUser } = useStore.getState();
    if (!jwt) throw error;
    // if 401 error retry the request with a refreshed token and log user out if it fails
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      try {
        // attempt to get a new JWT token
        const { token: newToken } = await this.refreshToken(jwt);
        this.instance.defaults.headers.common.Authorization = `JWT ${newToken}`;
        setJwt(newToken);

        // retry the original request
        return await this.instance.request(requestData);
      } catch (refreshError) {
        // still failed, log user out
        if (
          axios.isAxiosError(refreshError) &&
          refreshError.response?.status === 401
        ) {
          await this.instance.post("/api/auth/logout/");
          setJwt(undefined);
          setUser(undefined);
        }
        throw refreshError;
      }
    } else {
      throw error;
    }
  }

  /**
   * Adds a request interceptor for platforms using session/cookie auth (WEB and DESKTOP).
   * Strips the Www-Authenticate header to prevent 401 errors when using cookie-based auth.
   */
  private static addSessionAuthRequestInterceptor() {
    this.instance.interceptors.request.use(
      (config) => {
        // Create a copy of the config object
        const newConfig = { ...config };

        // Remove the Www-Authenticate header if it contains JWT realm="api"
        // This is necessary because authentication is based on cookies, not headers.
        // Having this header set results in a 401 error.
        if (
          newConfig.headers["Www-Authenticate"] &&
          newConfig.headers["Www-Authenticate"].includes('JWT realm="api"')
        ) {
          delete newConfig.headers["Www-Authenticate"];
        }

        return newConfig;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }
}

export default HttpRequestFactory;
